import * as React from 'react';

import classNames from 'classnames';

import {appHistory} from '../../../../../../history';

import {colors} from '../../colors/colors';
import {CancelCircleIcon} from '../../icon/svg/cancel-circle-icon';
import {ClockRegularIcon} from '../../icon/svg/clock-regular-icon';
import {EnvelopeRegularUserIcon} from '../../icon/svg/envelope-regular-user-icon';
import {PencilOutlineIcon} from '../../icon/svg/pencil-outline-icon';
import {PlayDoubleIcon} from '../../icon/svg/play-double-icon';
import {UserOutlineIcon} from '../../icon/svg/user-outline-icon';
import {WarningIcon} from '../../icon/svg/warning-icon';
import {Stack} from '../../layout/stack/stack';
import {Toolbar} from '../../toolbar/toolbar';
import {Body} from '../../typography/body';
import {useResizeObserver} from '../../utils/hooks/use-resize-observer';

import {AutomatedEmailRow} from '../automated-email-row/automated-email-row';

import type {PersonalEmailSequenceToView} from './types';

import styles from '../drip-sequence-row/drip-sequence-row.module.css';

export type SendFromUser = {
    id: string;
    name: string;
};

type Props = {
    personalEmailSequence: PersonalEmailSequenceToView;
    userId: string | null | undefined;
    htmlUrl: string;
    onCancelButtonClick: (sequenceId: string, buttonRef?: HTMLElement | null | undefined) => void;
    onStartButtonClick: (sequenceId: string, buttonRef?: HTMLElement | null | undefined) => void;
    onViewMessage: (emailId: string) => void;
    onSendEmailNow: (emailId: string) => void;
    forceShowToolbar?: boolean | null | undefined;
};

export function PersonalEmailSequenceRow(props: Props) {
    const {onCancelButtonClick, onStartButtonClick} = props;

    const [shouldShowToolbar, setShouldShowToolbar] = React.useState<boolean>(false);
    const [lineHeight, setLineHeight] = React.useState<number>(0);

    const emailRowsRef = React.useRef<HTMLElement | null | undefined>(null);
    const cancelButtonRef = React.useRef<HTMLElement | null | undefined>(null);
    const startButtonRef = React.useRef<HTMLElement | null | undefined>(null);

    const [width] = useResizeObserver(emailRowsRef.current);

    React.useEffect(() => {
        if (emailRowsRef.current) {
            const height = emailRowsRef.current.offsetHeight;
            setLineHeight(height - 12);
        }
    }, [props.personalEmailSequence.emails, width]);

    const toolbarOptions = React.useMemo(() => {
        const options = [];
        if (props.personalEmailSequence.canCancel) {
            options.push({
                id: 'cancel',
                icon: CancelCircleIcon,
                tooltipText: 'Cancel sequence',
                onClick: () => {
                    onCancelButtonClick(props.personalEmailSequence.id, cancelButtonRef.current);
                },
                // @ts-expect-error
                getRef: (ref) => {
                    cancelButtonRef.current = ref;
                },
            });
        }
        if (props.personalEmailSequence.canStart) {
            options.push({
                id: 'start',
                icon: PlayDoubleIcon,
                tooltipText: 'Start sequence',
                onClick: () => {
                    onStartButtonClick(props.personalEmailSequence.id, startButtonRef.current);
                },
                // @ts-expect-error
                getRef: (ref) => {
                    startButtonRef.current = ref;
                },
            });
        }
        options.push({
            id: 'edit',
            icon: PencilOutlineIcon,
            tooltipText: 'Edit sequence',
            onClick: () => {
                appHistory.push(props.htmlUrl);
            },
        });

        return options;
    }, [
        props.personalEmailSequence.id,
        onCancelButtonClick,
        cancelButtonRef,
        onStartButtonClick,
        startButtonRef,
        props.personalEmailSequence.canCancel,
        props.personalEmailSequence.canStart,
        props.htmlUrl,
    ]);

    const gridStyleName = classNames(styles['drip-sequence-grid'], {
        [styles['drip-sequence-grid-problem']]: props.personalEmailSequence.hasProblem,
    });

    return (
        <>
            <div className={gridStyleName}>
                <div className={styles['drip-icon-container']}>
                    {/* @ts-expect-error */}
                    <EnvelopeRegularUserIcon color={colors.greyLt} size={20} />
                </div>
                <div
                    onMouseOver={() => setShouldShowToolbar(true)}
                    onMouseLeave={() => setShouldShowToolbar(false)}
                    className={styles['header-container']}
                >
                    <Body isBold={true}>
                        {props.personalEmailSequence.title || 'Personal email sequence'}
                    </Body>

                    {props.personalEmailSequence.problemDescription ? (
                        <Stack spacing={4} isHorizontal={true} alignItems='center'>
                            {/* @ts-expect-error */}
                            <WarningIcon color={colors.yellow} wrapWithDiv={false} size={14} />
                            <Body size='small'>
                                {props.personalEmailSequence.problemDescription}
                            </Body>
                        </Stack>
                    ) : (
                        <Stack spacing={4}>
                            <Body size='small'>
                                {props.personalEmailSequence.stopSendCondition}
                            </Body>
                            <Stack spacing={8} isHorizontal={true}>
                                <Stack spacing={4} isHorizontal={true} alignItems='center'>
                                    <ClockRegularIcon
                                        size={16}
                                        // @ts-expect-error
                                        color={colors.grey}
                                        wrapWithDiv={false}
                                    />
                                    <Body size='small'>
                                        {props.personalEmailSequence.startTrigger}
                                    </Body>
                                </Stack>
                                {props.personalEmailSequence.sendFromUser && (
                                    <Stack spacing={4} isHorizontal={true} alignItems='center'>
                                        <UserOutlineIcon
                                            size={14}
                                            wrapWithDiv={false}
                                            // @ts-expect-error
                                            color={colors.grey}
                                        />
                                        <Body size='small'>
                                            Sending from{' '}
                                            {props.personalEmailSequence.sendFromUser.name}
                                        </Body>
                                    </Stack>
                                )}
                            </Stack>
                            {props.personalEmailSequence.recipientNames && (
                                <Stack spacing={4} isHorizontal={true} alignItems='center'>
                                    {/* @ts-expect-error */}
                                    <UserOutlineIcon size={14} color={colors.grey} />
                                    <Body size='small'>
                                        {props.personalEmailSequence.isCompleted
                                            ? 'Sent to'
                                            : 'Sending to'}{' '}
                                        {props.personalEmailSequence.recipientNames.join(', ')}
                                    </Body>
                                </Stack>
                            )}
                        </Stack>
                    )}
                    {(shouldShowToolbar || props.forceShowToolbar) && (
                        <div className={styles['toolbar-container']}>
                            {/* @ts-expect-error */}
                            <Toolbar options={toolbarOptions} />
                        </div>
                    )}
                </div>
                {!props.personalEmailSequence.hasProblem && (
                    <>
                        <div className={styles['vertical-line-container']}>
                            <div
                                style={{height: `${lineHeight}px`}}
                                className={styles['left-vertical-line']}
                            />
                        </div>
                        {/* @ts-expect-error */}
                        <div ref={emailRowsRef} className={styles['email-rows-cell']}>
                            {props.personalEmailSequence.emails &&
                                props.personalEmailSequence.emails.map((queuedEmail) => (
                                    <AutomatedEmailRow
                                        key={queuedEmail.id}
                                        id={queuedEmail.id}
                                        title={queuedEmail.subject}
                                        sent={queuedEmail.isAlreadySent}
                                        scheduledTime={queuedEmail.deliveryTime}
                                        sendTime={
                                            queuedEmail.isAlreadySent
                                                ? queuedEmail.deliveryTime
                                                : null
                                        }
                                        onViewMessage={props.onViewMessage}
                                        onSendNow={
                                            queuedEmail.isNextScheduledEmail &&
                                            !props.personalEmailSequence.problemDescription
                                                ? () => props.onSendEmailNow(queuedEmail.id)
                                                : undefined
                                        }
                                        editUrl={
                                            props.personalEmailSequence.sendFromUser &&
                                            props.personalEmailSequence.sendFromUser.id ===
                                                props.userId
                                                ? `#/edit-scheduled-email/${queuedEmail.id}`
                                                : undefined
                                        }
                                    />
                                ))}
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
